import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { getInvite, completeSetup } from '../api';
import { useAuth, getHomePath } from '../context/AuthContext';
import LoadingState from '../components/LoadingState';
import AuthShell, { AuthField, CodeInput } from '../components/AuthShell';

const POLICY_CHECKS = [
  ['length', 'Mindst 12 tegn', (p) => p.length >= 12],
  ['lower', 'Mindst ét lille bogstav', (p) => /[a-z]/.test(p)],
  ['upper', 'Mindst ét stort bogstav', (p) => /[A-Z]/.test(p)],
  ['digit', 'Mindst ét tal', (p) => /[0-9]/.test(p)],
  ['symbol', 'Mindst ét specialtegn', (p) => /[^a-zA-Z0-9]/.test(p)],
];

export default function SetupAccountPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { completeLocalLogin } = useAuth();

  const [invite, setInvite] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [loadError, setLoadError] = useState('');
  // The account is set up but a new account must be activated by an administrator before anyone can sign in.
  const [awaitingApproval, setAwaitingApproval] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token) {
      setLoadError('Linket mangler en gyldig kode.');
      return;
    }
    let cancelled = false;
    getInvite(token).then(async (result) => {
      if (cancelled) return;
      if (result.error) {
        setLoadError(result.error);
        return;
      }
      setInvite(result);
      if (!result.otpauthUrl) return; // a password reset keeps the existing authenticator
      try {
        const dataUrl = await QRCode.toDataURL(result.otpauthUrl, { width: 200, margin: 1 });
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch {
        // The setup form still works without the QR image — the secret is
        // in the otpauthUrl either way, just less convenient to enter by
        // hand into an authenticator app.
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // setup = new account, password = password reset (keeps the authenticator),
  // mfa = authenticator reset (keeps the password)
  const mode = invite?.mode || 'setup';
  const needsNewPassword = mode !== 'mfa';
  const showQr = mode !== 'password';

  const policyResults = useMemo(() => POLICY_CHECKS.map(([key, label, test]) => [key, label, test(password)]), [password]);
  const policyMet = policyResults.every(([, , ok]) => ok);
  const passwordsMatch = password.length > 0 && password === confirmPassword;

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError('');
    if (needsNewPassword && !policyMet) {
      setSubmitError('Adgangskoden opfylder ikke alle krav endnu.');
      return;
    }
    if (needsNewPassword && !passwordsMatch) {
      setSubmitError('Adgangskoderne er ikke ens.');
      return;
    }
    if (mode === 'mfa' && !currentPassword) {
      setSubmitError('Indtast din nuværende adgangskode.');
      return;
    }
    if (totpCode.trim().length !== 6) {
      setSubmitError('Indtast den 6-cifrede kode fra din godkender-app.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await completeSetup(token, needsNewPassword ? { password, totpCode: totpCode.trim() } : { currentPassword, totpCode: totpCode.trim() });
      if (!result.success) {
        setSubmitError(result.error || 'Kunne ikke oprette kontoen. Prøv igen.');
        return;
      }
      if (result.pending) {
        setAwaitingApproval(true);
        return;
      }
      completeLocalLogin(result);
      navigate(getHomePath(result.user.role, result.user.tenantIds));
    } catch (err) {
      console.error('Complete setup error:', err);
      setSubmitError('Kunne ikke oprette kontoen. Prøv igen.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell>
      {awaitingApproval ? (
        <>
          <h1>Din konto er sat op</h1>
          <p className="au-sub">Den skal aktiveres af en administrator, før du kan logge ind. Du får besked, når det er sket.</p>
        </>
      ) : loadError ? (
        <>
          <h1>Linket virker ikke</h1>
          <p className="au-msg" role="alert">{loadError}</p>
        </>
      ) : !invite ? (
        <LoadingState />
      ) : (
        <>
          <h1>{mode === 'password' ? 'Vælg en ny adgangskode' : mode === 'mfa' ? 'Tilknyt din godkender-app igen' : 'Opret din adgang'}</h1>
          <p className="au-sub">
            Hej {invite.name}{invite.tenantName ? ` — ${invite.tenantName}` : ''}.{' '}
            {mode === 'password'
              ? 'Vælg en ny adgangskode. Du skal bruge koden fra din nuværende godkender-app for at bekræfte, at det er dig.'
              : mode === 'mfa'
              ? 'Indtast din nuværende adgangskode og tilknyt en ny godkender-app. Din gamle app virker ikke længere.'
              : 'Vælg en adgangskode og tilknyt din godkender-app. MFA er påkrævet.'}
          </p>

          <form onSubmit={handleSubmit}>
            {mode === 'mfa' && (
              <AuthField label="Nuværende adgangskode" icon="lock">
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={submitting}
                  autoComplete="current-password"
                  autoFocus
                />
              </AuthField>
            )}
            {needsNewPassword && (
              <>
                <AuthField label="Ny adgangskode" icon="lock">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={submitting}
                    autoComplete="new-password"
                    autoFocus
                  />
                </AuthField>
                <AuthField label="Gentag adgangskode" icon="lock" bad={confirmPassword.length > 0 && !passwordsMatch}>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={submitting}
                    autoComplete="new-password"
                  />
                </AuthField>
                <ul className="au-rules">
                  {policyResults.map(([key, label, ok]) => (
                    <li key={key} className={ok ? 'au-met' : ''}><i />{label}</li>
                  ))}
                </ul>
              </>
            )}

            {showQr && (
              <div className="au-qr">
                {qrDataUrl && <img src={qrDataUrl} alt="QR-kode til godkender-app" width={92} height={92} />}
                <p><b>Scan med din godkender-app</b><br />Microsoft Authenticator eller lignende. Indtast derefter koden herunder.</p>
              </div>
            )}
            <div className="au-field">
              <span>{mode === 'password' ? 'Kode fra din nuværende godkender-app' : 'Kode fra appen'}</span>
              <CodeInput value={totpCode} onChange={setTotpCode} disabled={submitting} />
            </div>

            {submitError && <p className="au-msg" role="alert">{submitError}</p>}
            <button type="submit" className="au-btn" disabled={submitting}>
              {submitting ? 'Gemmer…' : mode === 'password' ? 'Gem ny adgangskode og log ind' : mode === 'mfa' ? 'Tilknyt og log ind' : 'Opret adgang og log ind'}
            </button>
          </form>
        </>
      )}
    </AuthShell>
  );
}
